import React from 'react';
import { ArrowLeft, Search, X, Clock, ChevronRight } from 'lucide-react';

// Modal de Busca de produtos do cardapio publico.
// Extraido VERBATIM de PublicMenu.js (Fase 2 refactor). Componente puro (sem hooks),
// recebe estado/handlers/ref via props. Renderizado como <SearchModal .../>.
const SearchModal = ({
  showSearchModal,
  closeSearch,
  searchInputRef,
  searchQuery,
  handleSearch,
  setSearchQuery,
  setSearchResults,
  searchResults,
  navigateToScreen,
}) => {
    if (!showSearchModal) return null;
    
    return (
      <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm" onClick={closeSearch}>
        <div 
          className="absolute top-0 left-0 right-0 bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight: '90vh', overflowY: 'auto' }}
        >
          {/* Search Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <button 
                onClick={closeSearch}
                className="p-2 hover:bg-gray-100 rounded-full transition-all active:scale-95"
              >
                <ArrowLeft size={20} />
              </button>
              
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search 
                  size={18} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" 
                />
                <input
                  ref={searchInputRef}
                  id="search-input"
                  type="text"
                  placeholder="Buscar produtos..."
                  value={searchQuery}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults([]);
                      searchInputRef.current?.focus();
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                  >
                    <X size={16} className="text-gray-500" />
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Search Results */}
          <div className="p-4">
            {searchQuery.trim() === '' ? (
              <div className="text-center py-12">
                <Search size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">Digite para buscar produtos</p>
                <p className="text-sm text-gray-400 mt-1">Ex: hambúrguer, pizza, suco...</p>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-3">😕</div>
                <p className="text-gray-700 font-medium">Nenhum produto encontrado</p>
                <p className="text-sm text-gray-500 mt-1">Tente buscar com outros termos</p>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold" style={{ color: 'var(--color-primary, #3B82F6)' }}>
                      {searchResults.length}
                    </span>
                    {' '}resultado{searchResults.length !== 1 ? 's' : ''} encontrado{searchResults.length !== 1 ? 's' : ''}
                  </p>
                </div>
                
                <div className="space-y-3">
                  {searchResults.map(product => (
                    <div
                      key={product.id}
                      onClick={() => {
                        navigateToScreen('product', product);
                        closeSearch();
                      }}
                      className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3 cursor-pointer hover:border-primary hover:shadow-md transition-all active:scale-98"
                    >
                      {/* Product Image */}
                      <img
                        src={product.image_url || "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg"}
                        alt={product.name}
                        className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.target.src = "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg";
                        }}
                      />
                      
                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 mb-1 truncate">
                          {product.name}
                        </h3>
                        <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                          {product.description || 'Produto delicioso'}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                              {product.categoryName}
                            </span>
                            {product.preparation_time && (
                              <span className="text-xs text-gray-400 flex items-center gap-0.5">
                                <Clock size={10} />
                                {product.preparation_time}min
                              </span>
                            )}
                          </div>
                          <span className="text-lg font-bold" style={{ color: 'var(--color-primary, #3B82F6)' }}>
                            {product.price > 0 ? `R$ ${product.price.toFixed(2)}` : 'Ver opções'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Arrow */}
                      <ChevronRight size={20} className="text-gray-400 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

export default SearchModal;
